<script lang="ts">
	import type { PoiImages } from '$lib/components/pois/types';
	import type { ClassValue } from 'svelte/elements';
	import Button from '$lib/components/atoms/button/Button.svelte';
	import PoiImage from '$lib/components/pois/image/PoiImage.svelte';
	import { PREFETCH_IMAGES_COUNT } from '$lib/constants/poilist';
	import { preventDefaultAndPropagation } from '$lib/utils/preventDefaultAndPropagation';
	import * as m from '$paraglide/messages';
	import { watch } from 'runed';
	import { type Snippet, tick } from 'svelte';
	import { debounce } from 'throttle-debounce';

	const DEBOUNCE_USER_RESIZE = 100;
	const DEBOUNCE_USER_SCROLL = 10;
	const INDICATORS_MAX_IMAGES_PER_LINES = 15;
	const SLIDE_END_TIMEOUT = 100;

	interface Props {
		images: PoiImages[];
		navigationIcons?: { left: 'chevron-left' | 'west'; right: 'chevron-right' | 'east' };
		prefixAlt?: string;
		carouselClasses?: ClassValue;
		containerClasses?: ClassValue;
		controlsClasses?: ClassValue;
		withEagerImages?: boolean;
		fallbackSrc?: string;
		// Note: don't set ariaLabel to 'carousel' as aria-roledescription is already set to 'carousel', which will be redundant.
		ariaLabel?: string;
		imageSnippet?: Snippet<[PoiImages, number, string, boolean, string]>;
		enableFullscreen?: boolean;
		isCarouselOpen?: boolean;
		enableIndicators?: boolean;
		navigationType?: 'full' | 'minimal';
		enableImageCopyright?: boolean;
		onImageClick?: (carouselOpen: boolean) => void;
		onImageScroll?: () => void;
	}

	let {
		images,
		prefixAlt,
		carouselClasses,
		containerClasses,
		controlsClasses,
		withEagerImages = false,
		fallbackSrc,
		ariaLabel = 'Image carousel',
		onImageScroll,
		imageSnippet,
		enableFullscreen = false,
		isCarouselOpen = $bindable(false),
		onImageClick,
		navigationIcons = { left: 'chevron-left', right: 'chevron-right' },
		enableIndicators = false,
		navigationType = 'full',
		enableImageCopyright = false,
	}: Props = $props();

	let currentIndex = $state(0);
	let carouselContainer: HTMLElement | undefined = $state();
	let isScrolling: NodeJS.Timeout | undefined = $state();

	function onSlideEnd(callback: () => void, timeout: number = SLIDE_END_TIMEOUT) {
		window.clearTimeout(isScrolling);
		isScrolling = setTimeout(callback, timeout);
	}

	function handleTouch() {
		if (currentIndex === images.length - 1 && !isScrolling && carouselContainer) {
			onSlideEnd(() => {
				carouselContainer?.scrollTo({
					left: 0,
					behavior: 'smooth',
				});
				currentIndex = 0;
				isScrolling = undefined;
			});
		}
	}

	const handleScroll = debounce(DEBOUNCE_USER_SCROLL, (e) => {
		e.preventDefault();

		if (carouselContainer) {
			const scrollLeft = carouselContainer.scrollLeft;
			const totalWidth = carouselContainer.getBoundingClientRect().width;
			const newIndex = Math.round(scrollLeft / totalWidth);

			if (currentIndex === newIndex && currentIndex === images.length - 1 && !isScrolling) {
				carouselContainer?.scrollTo({
					left: 0,
					behavior: 'smooth',
				});
				currentIndex = 0;
				return;
			}

			onSlideEnd(() => {
				currentIndex = newIndex;
				isScrolling = undefined;
			});
		}
	});

	const handleResize = debounce(DEBOUNCE_USER_RESIZE, () => {
		scrollToIndexImage(currentIndex);
	});

	function scrollImage(direction: 'right' | 'left' = 'right') {
		if (carouselContainer) {
			const totalWidth = carouselContainer.getBoundingClientRect().width;
			let scrollDirectionIndex = 0;

			if (direction === 'right') {
				scrollDirectionIndex = currentIndex === images.length - 1 ? 0 : currentIndex + 1;
			}
			if (direction === 'left') {
				scrollDirectionIndex = currentIndex - 1;
			}

			carouselContainer.scrollTo({
				left: totalWidth * scrollDirectionIndex,
				behavior: 'smooth',
			});
		}
	}

	function scrollToIndexImage(index: number) {
		if (carouselContainer) {
			const carouselContainerWidth = carouselContainer.getBoundingClientRect().width;
			carouselContainer.scrollTo({
				left: 0,
				behavior: 'instant',
			});

			carouselContainer.scrollTo({
				left: carouselContainerWidth * index,
				behavior: 'instant',
			});
		}
	}

	async function toggleModal(status: 'open' | 'close' = 'open') {
		isCarouselOpen = status === 'open';
		await tick();
		scrollToIndexImage(currentIndex);
	}

	watch(() => images, () => {
		if (images && enableFullscreen) {
			scrollToIndexImage(0);
		}
	});
</script>

<svelte:window onresize={handleResize} />

<svelte:element
	this={enableFullscreen && isCarouselOpen ? 'dialog' : 'div'}
	class={[
		'container-carousel',
		isCarouselOpen ? 'full-screen bg-coloredneutral inset-0 position-fixed w-screen h-full z-1 p-0 z-float' : 'w-full relative cursor-pointer',
		navigationType,
		containerClasses,
	]}
	role={enableFullscreen && isCarouselOpen ? 'dialog' : 'region'}
	aria-roledescription="carousel"
	aria-label={ariaLabel}
	open={isCarouselOpen && enableFullscreen}
>
	{#if isCarouselOpen && enableFullscreen}
		<Button
			class="absolute top-2 left-2 z-1"
			color="white"
			hideTextOnMobile
			icon="west"
			onclick={() => toggleModal('close')}
			onmousedown={preventDefaultAndPropagation}
			onpointerdown={preventDefaultAndPropagation}
			size="md"
			text={m.poi_datasheet__carousel__close_button()}
			variant="filled"
		/>
	{/if}

	<div class={isCarouselOpen && enableFullscreen ? 'h-full' : enableFullscreen ? 'h-70' : 'h-auto'}>
		<Button
			color="white"
			icon={navigationIcons.left}
			size="md"
			variant="ghost"
			ariaLabel={m.poi_card__carousel__prev_button()}
			ariaHidden={currentIndex === 0}
			class={[
				'left-arrow-container absolute left-0 hidden',
				currentIndex > 0 && 'md:block',
				controlsClasses,
			]}
			onclick={(e) => {
				preventDefaultAndPropagation(e);
				scrollImage('left');
			}}
			onmousedown={preventDefaultAndPropagation}
			onpointerdown={preventDefaultAndPropagation}
		/>
		<!-- eslint-disable-next-line svelte/valid-compile -->
		<ul
			class={[
				'carousel flex h-100% overflow-x-scroll m-0 p-0 list-none',
				isCarouselOpen ? 'bg-coloredneutral' : 'bg-neutral-weakest',
				carouselClasses,
			]}
			aria-roledescription="slide"
			role="group"
			aria-label={m.poi_card__carousel__list_aria_label({
				current: currentIndex + 1,
				total: images.length,
			})}
			onscroll={handleScroll}
			onscrollend={() => onImageScroll?.()}
			ontouchstart={handleTouch}
			ontouchend={handleTouch}
			bind:this={carouselContainer}
		>
			{#each images as image, index (`${image}-${index}`)}
				<li
					class={['img-container items-center flex grow-0 shrink-0 justify-center w-full', enableFullscreen && 'cursor-pointer']}
					{...(enableFullscreen && {
						'onclick': (e) => {
							e.stopPropagation();
							onImageClick?.(isCarouselOpen);
							toggleModal('open');
						},
						'onkeypress': (event) => {
							if (event.key === ' ') {
								toggleModal('open');
							}
						},
						'role': 'button',
						'tabindex': 0,
						'data-testid': `image-button-${index}`,
					})}>
					<figure class={['img-wrapper w-full m-0 p-0', isCarouselOpen ? 'h-fit md:flex md:h-full md:w-auto md:flex-row-reverse items-end justify-center' : 'h-full overflow-hidden']}>
						{#if imageSnippet}
							{@render imageSnippet(image, index, prefixAlt ?? '', isCarouselOpen, fallbackSrc ?? '')}
						{:else}
							<PoiImage
								srcset={image}
								eager={withEagerImages ? index < PREFETCH_IMAGES_COUNT : false}
								alt={`${prefixAlt} - ${index}`}
								class={[isCarouselOpen ? 'h-fit md:h-full w-full md:w-auto md:px-5' : 'h-full w-full']}
								objectFit={isCarouselOpen && enableFullscreen ? 'contain' : 'cover'}
								{fallbackSrc}
								{...(!enableFullscreen && { height: '132px', width: '260px', sizes: '320px' })}
							/>
							{#if enableImageCopyright && image.copyright}
								<span
									class={[
										'img-copyright p-2 text-neutral-soft md:translate-x-6 md:translate-y--14 md:translate-z-0 md:transform-scale--100',
										isCarouselOpen && index === currentIndex ? 'visible' : 'invisible',
									]}
								>
									<small>{image.copyright}</small>
								</span>
							{/if}
						{/if}
					</figure>
				</li>
			{/each}
		</ul>

		{#if enableIndicators && images.length > 1}
			<div
				data-testid="indicators"
				class={[
					'indicators flex-wrap items-center bottom-0 flex h-14 justify-center left-50% absolute transform-translate-x--50%',
					images.length > INDICATORS_MAX_IMAGES_PER_LINES && 'transform-translate-y--25%',
				]}
			>
				{#each images as _, index (`indicator-${index}`)}
					<span
						data-testid={`indicator-${index}`}
						class={['rounded-full inline-block h-2 mx-1 w-2 transition-all cursor-default', index === currentIndex && 'active']}
					></span>
				{/each}
			</div>
		{/if}

		<Button
			color="white"
			icon={navigationIcons.right}
			size="md"
			variant="ghost"
			ariaLabel={m.poi_card__carousel__next_button()}
			ariaHidden={images.length === 0}
			class={[
				'right-arrow-container absolute right-0 hidden',
				images.length > 1 && 'md:block',
				controlsClasses,
			]}
			onclick={(e) => {
				preventDefaultAndPropagation(e);
				scrollImage('right');
			}}
			onmousedown={preventDefaultAndPropagation}
			onpointerdown={preventDefaultAndPropagation}
		/>
	</div>
</svelte:element>

<style>
	.carousel {
		-ms-overflow-style: none;
		scrollbar-width: none;
		scroll-snap-type: x mandatory;
		scroll-behavior: smooth;
	}

	.carousel::-webkit-scrollbar {
		display: none;
	}

	.container-carousel :global(.left-arrow-container),
	.container-carousel :global(.right-arrow-container) {
		top: 50%;
		transform: translateY(-50%);

	}

	.container-carousel.minimal :global(.left-arrow-container),
	.container-carousel.minimal :global(.right-arrow-container) {
		background-color: rgba(0, 0, 0, 0.1);
	}

	.container-carousel.full :global(.left-arrow-container) {
		background: linear-gradient(90deg, rgba(29, 49, 64, 0.5) 0%, rgba(29, 49, 64, 0) 100%);
		height: 100%;
	}
	.container-carousel.full :global(.right-arrow-container) {
		background: linear-gradient(-90deg, rgba(29, 49, 64, 0.5) 0%, rgba(29, 49, 64, 0) 100%);
		height: 100%;
	}

 /* using !important is necessary here to avoid applying global snap style used for handles */
	.img-container,
	.img-container :global(*) {
		scroll-snap-align: center !important;
		scroll-margin: 0 !important;
	}

	.img-copyright {
		@media screen and (min-width: 960px) {
			writing-mode: vertical-rl;
			text-orientation: mixed;
		}
	}

	@media (hover: hover) and (pointer: fine) {
		.carousel :global(.left-arrow-container:not(.hidden)),
		.carousel :global(.right-arrow-container:not(.hidden)) {
			display: block;
		}
	}

	.indicators span {
		background-color: rgba(255, 255, 255, 0.6);
	}

	.indicators span.active {
		background-color: white;
	}
</style>
